import { useState, useMemo } from "react";
import { Modal, Select, Input, Typography, Divider, theme } from "antd";
import { useTranslation } from "react-i18next";
import type { ItemPendingActivityInfo } from "../../types";

const { Text } = Typography;
const { TextArea } = Input;

interface KanbanVoteModalProps {
  open: boolean;
  activity: ItemPendingActivityInfo;
  loading: boolean;
  onSubmit: (pathId: string, pathName: string, comments?: string) => void;
  onCancel: () => void;
}

const KanbanVoteModal: React.FC<KanbanVoteModalProps> = ({
  open,
  activity,
  loading,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [comments, setComments] = useState("");

  const pathOptions = useMemo(
    () =>
      activity.paths.map((p) => ({
        label: p.label || p.name,
        value: p.id,
      })),
    [activity.paths],
  );

  // Auto-select the default path
  const defaultPathId = useMemo(() => {
    const defaultPath = activity.paths.find((p) => p.isDefault);
    return defaultPath?.id ?? null;
  }, [activity.paths]);

  // Use selected or default
  const activePathId = selectedPath ?? defaultPathId;

  const handleOk = () => {
    if (!activePathId) return;
    const pathObj = activity.paths.find((p) => p.id === activePathId);
    if (!pathObj) return;
    onSubmit(
      activePathId,
      pathObj.name || pathObj.label || "",
      comments || undefined,
    );
  };

  const handleCancel = () => {
    setSelectedPath(null);
    setComments("");
    onCancel();
  };

  return (
    <Modal
      title={t("kanban.voteTitle", "Workflow Vote Required")}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okButtonProps={{ disabled: !activePathId }}
      okText={t("actions.complete", "Complete")}
      cancelText={t("actions.cancel", "Cancel")}
      width={480}
      destroyOnClose
    >
      {/* Activity info */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t("workflow.activity", "Activity")}:</Text>{" "}
        <Text>{activity.activityName}</Text>
      </div>

      {/* Instructions */}
      {activity.instructions && (
        <>
          <div
            style={{
              marginBottom: 16,
              padding: "8px 12px",
              backgroundColor: token.colorFillQuaternary,
              borderRadius: 4,
              borderLeft: `3px solid ${token.colorPrimary}`,
            }}
          >
            <Text>{activity.instructions}</Text>
          </div>
        </>
      )}

      <Divider style={{ margin: "12px 0" }} />

      {/* Path selection */}
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          {t("workflow.vote", "Vote")}:
        </Text>
        <Select
          style={{ width: "100%" }}
          placeholder={t("workflow.selectPathPlaceholder", "Select a path...")}
          value={activePathId}
          onChange={setSelectedPath}
          options={pathOptions}
        />
      </div>

      {/* Comments */}
      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          {t("workflow.comments", "Comments")}:
        </Text>
        <TextArea
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder={t(
            "workflow.commentsPlaceholder",
            "Optional comments...",
          )}
        />
      </div>
    </Modal>
  );
};

export default KanbanVoteModal;
